import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { Button, Badge, Modal } from '../../components/ui';
import { pageStyles, tableStyles, cardStyles } from '../../lib/styles';
import { StaffForm } from '../../components/forms/StaffForm';
import { PasswordInput } from '../../components/forms/PasswordInput';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import type { StaffSummary } from '@leal-crm/shared';

type ModalType = 'create' | 'edit' | 'resetPassword' | 'confirmDeactivate' | null;

export function StaffManagementPage() {
  const { t } = useTranslation();
  const { success: showSuccess, error: showError } = useToast();
  const { user } = useAuth();

  // Admin-only access
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  const [staffList, setStaffList] = useState<StaffSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedStaff, setSelectedStaff] = useState<StaffSummary | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchStaff = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<{ staff: StaffSummary[]; total: number }>(
        `/api/admin/staff${includeInactive ? '?includeInactive=true' : ''}`
      );
      setStaffList(response.staff);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, [includeInactive]);

  const handleCreateSuccess = (staff: StaffSummary) => {
    setStaffList((prev) => [staff, ...prev]);
    setModalType(null);
  };

  const handleEditSuccess = (staff: StaffSummary) => {
    setStaffList((prev) => prev.map((s) => (s.id === staff.id ? staff : s)));
    setModalType(null);
    setSelectedStaff(null);
  };

  const handleDeactivate = async () => {
    if (!selectedStaff) return;

    try {
      setIsSubmitting(true);
      await api.post(`/api/admin/staff/${selectedStaff.id}/deactivate`);
      setStaffList((prev) =>
        prev.map((s) => (s.id === selectedStaff.id ? { ...s, active: false } : s))
      );
      showSuccess(t('staff.deactivated'));
      setModalType(null);
      setSelectedStaff(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      showError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivate = async (staff: StaffSummary) => {
    try {
      await api.post(`/api/admin/staff/${staff.id}/activate`);
      setStaffList((prev) =>
        prev.map((s) => (s.id === staff.id ? { ...s, active: true } : s))
      );
      showSuccess(t('staff.activated'));
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      showError(message);
    }
  };

  const handleUnlock = async (staff: StaffSummary) => {
    try {
      await api.post(`/api/admin/staff/${staff.id}/unlock`);
      showSuccess(t('staff.unlocked'));
      fetchStaff();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      showError(message);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedStaff) return;

    if (!newPassword || newPassword.length < 8) {
      setPasswordError(t('auth.passwordRequirements'));
      return;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setPasswordError(t('auth.passwordRequirements'));
      return;
    }

    try {
      setIsSubmitting(true);
      await api.post(`/api/admin/staff/${selectedStaff.id}/reset-password`, {
        newPassword,
        sendEmail: true,
      });
      showSuccess(t('staff.passwordReset'));
      setModalType(null);
      setSelectedStaff(null);
      setNewPassword('');
      setPasswordError('');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      showError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (staff: StaffSummary) => {
    setSelectedStaff(staff);
    setModalType('edit');
  };

  const openDeactivateModal = (staff: StaffSummary) => {
    setSelectedStaff(staff);
    setModalType('confirmDeactivate');
  };

  const openResetPasswordModal = (staff: StaffSummary) => {
    setSelectedStaff(staff);
    setNewPassword('');
    setPasswordError('');
    setModalType('resetPassword');
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedStaff(null);
    setNewPassword('');
    setPasswordError('');
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'purple';
      case 'reception':
        return 'blue';
      case 'workshop':
        return 'green';
      default:
        return 'gray';
    }
  };

  return (
    <div className={pageStyles.container}>
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>{t('staff.title')}</h1>
        </div>
        <Button onClick={() => setModalType('create')}>{t('staff.addStaff')}</Button>
      </div>

      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              id="includeInactive"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-xs text-gray-700">
              {t('staff.inactive')} ({t('common.filter')})
            </span>
          </label>
        </div>
        <div className={cardStyles.content}>
          <div className={tableStyles.container}>
            {isLoading ? (
              <div className="p-3 space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-10 bg-gray-100 animate-pulse rounded" />
                ))}
              </div>
            ) : staffList.length === 0 ? (
              <div className="py-10 text-center text-xs text-gray-500">
                {t('common.noResults')}
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className={tableStyles.headerRow}>
                    <th className={tableStyles.headerCell}>{t('staff.name')}</th>
                    <th className={tableStyles.headerCell}>{t('staff.email')}</th>
                    <th className={tableStyles.headerCell}>{t('staff.role')}</th>
                    <th className={tableStyles.headerCell}>{t('staff.status')}</th>
                    <th className={`${tableStyles.headerCell} text-right`}>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className={tableStyles.body}>
                  {staffList.map((staff) => (
                    <tr key={staff.id} className={staff.active ? tableStyles.row : `${tableStyles.row} bg-gray-50`}>
                      <td className={tableStyles.cell}>
                        <div className="font-medium text-gray-900">{staff.name}</div>
                      </td>
                      <td className={`${tableStyles.cell} text-gray-500`}>{staff.email}</td>
                      <td className={tableStyles.cell}>
                        <Badge color={getRoleBadgeColor(staff.role)}>
                          {t(`staff.roles.${staff.role}`)}
                        </Badge>
                      </td>
                      <td className={tableStyles.cell}>
                        {staff.active ? (
                          <Badge color="green">{t('staff.active')}</Badge>
                        ) : (
                          <Badge color="red">{t('staff.inactive')}</Badge>
                        )}
                      </td>
                      <td className={`${tableStyles.cell} text-right`}>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(staff)}
                          >
                            {t('common.edit')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openResetPasswordModal(staff)}
                          >
                            {t('staff.resetPassword')}
                          </Button>
                          {staff.id !== user?.id && (
                            <>
                              {staff.active ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openDeactivateModal(staff)}
                                >
                                  {t('staff.deactivate')}
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleActivate(staff)}
                                >
                                  {t('staff.activate')}
                                </Button>
                              )}
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnlock(staff)}
                          >
                            {t('staff.unlock')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Create Staff Modal */}
      <Modal
        isOpen={modalType === 'create'}
        onClose={closeModal}
        title={t('staff.addStaff')}
        size="md"
      >
        <StaffForm onSuccess={handleCreateSuccess} onCancel={closeModal} />
      </Modal>

      {/* Edit Staff Modal */}
      <Modal
        isOpen={modalType === 'edit'}
        onClose={closeModal}
        title={t('staff.editStaff')}
        size="md"
      >
        {selectedStaff && (
          <StaffForm
            initialData={selectedStaff}
            staffId={selectedStaff.id}
            onSuccess={handleEditSuccess}
            onCancel={closeModal}
          />
        )}
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={modalType === 'resetPassword'}
        onClose={closeModal}
        title={t('staff.resetPassword')}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {selectedStaff?.name} ({selectedStaff?.email})
          </p>
          <PasswordInput
            label={t('auth.newPassword')}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setPasswordError('');
            }}
            error={passwordError}
            showRequirements
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={closeModal}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleResetPassword} loading={isSubmitting}>
              {t('staff.resetPassword')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirm Deactivate Modal */}
      <Modal
        isOpen={modalType === 'confirmDeactivate'}
        onClose={closeModal}
        title={t('staff.deactivate')}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">{t('staff.confirmDeactivate')}</p>
          <p className="font-medium text-gray-900">
            {selectedStaff?.name} ({selectedStaff?.email})
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={closeModal}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeactivate} loading={isSubmitting}>
              {t('staff.deactivate')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
